import {ApplicationRef, assertPlatform, Component, Directive, Input, OnDestroy, TemplateRef, ViewEncapsulation} from '@angular/core';
import {BaseComponent} from "../base/base.component";
import {OrderDayModel, OrderWeekModel} from "../model/dto.model";
import {DateUtilsBs} from "../base/utils/dateUtilsBs";
import {RestClient} from "../rest-client.service";
import {catchError, debounceTime, distinctUntilChanged, from, groupBy, map, mergeMap, Observable, of, Subject, Subscription, switchMap, tap, toArray, zip} from "rxjs";
import {OrdersState} from "./orders.state";
import {ActivatedRoute, NavigationEnd, Router, RouterEvent} from "@angular/router";
import {StringUtils} from "../base/utils/string.utils";
import {NumberUtils} from "../base/utils/number.utils";
import {AuthService} from '../base/auth/auth.service';
import {NotificationsService} from '../notifications/notifications.service';
import {MessageService} from 'primeng/api';
import { BsaOrderPlaceholdersXBVO, BsaOrderPlaceholderXBVO, MealTypeEnum } from '../model/dto.models.imported';
import {SaldoChangeXBVO, SaldoDetailsXBVO} from "../model/xbvo.model";


type KeyAndPlaceholder = {value: number, placeholder: BsaOrderPlaceholderXBVO};

/**
 * App-Component
 */
@Component({
    selector: '<skitbs-orders>',
    templateUrl: './orders.component.html'
})
export class OrdersComponent extends BaseComponent implements OnDestroy {

    modelYear: number;
    modelWeek: number;
    modelOrdersWeek: OrderWeekModel;
    modelPrepaidSaldo: number;
    modelMultipleOrders: boolean;
    delaySubject = new Subject<KeyAndPlaceholder>();
    delaySubjectSubscription : Subscription;

    saldoDetails: SaldoDetailsXBVO;

    @Input() public myTemplate: TemplateRef<any>;

    /**
     * Constructor.
     * @param restClient
     */
    constructor(
        private restClient: RestClient,
        private orderState: OrdersState,
        private router: Router,
        private auth: AuthService
    ) {
        super();

        // restore KW
        let pKW = this.orderState.getKW();
        this.modelYear = pKW[0];
        this.modelWeek = pKW[1];
        this.modelMultipleOrders = auth.findCurrentSession().placeMultipleOrdersPerMenulineAndServiceday;
    }

    /**
     * Lifecycle.
     */
    ngOnInit(): void {
        this.delaySubjectSubscription = this.delaySubject.pipe(
            debounceTime(800),
            distinctUntilChanged()
        )
        .subscribe(arg=>this.onOrderCountChange(arg.value, arg.placeholder));

        if (!this.modelOrdersWeek) {
            this.loadPlaceholders();
            this.getOrderDetails();
        }
    }

    ngOnDestroy(): void {
        this.delaySubjectSubscription.unsubscribe();
    }

    /**
     * REST - calls
     * @private
     */
    private loadPlaceholders() {
        this.restClient
            .orderPlaceholders(this.modelYear, this.modelWeek)
            .subscribe(aXBVO => this.convertModelPlaceholders(aXBVO));
    }

    private getOrderDetails() {
      this.restClient
        .getSaldoDetails()
        .subscribe(aXBVO => this.convertSaldo(aXBVO));
    }

    onOrderCountChange(val: number, aPlaceholder: BsaOrderPlaceholderXBVO) {
        if (aPlaceholder.disableOrdering) return;

        aPlaceholder.orderPlaced = val > 0;
        aPlaceholder.quantity = val;

        this.restClient
            .changeOrderCount(aPlaceholder)
            .pipe(
                catchError(aErrorResponse => {
                    this.loadPlaceholders();
                    this.getOrderDetails();
                    return this.restClient.onError(aErrorResponse);
                })
            )
            .subscribe(response => {
                this.toastInfo(this.v('orderPlaced'));
            });
    }

    onOrderCountKeypress(args: {event: any, placeholder: BsaOrderPlaceholderXBVO}) {
        this.delaySubject.next({value:args.event.target.value, placeholder: args.placeholder});
    }

    /**
     * REST - calls
     * @private
     */
    onPlaceOrder(aDay: OrderDayModel, aPlaceholder: BsaOrderPlaceholderXBVO) {
        if (aPlaceholder.disableOrdering || aPlaceholder.orderPlaced) {
            return;
        }
        this.restClient
            .placeOrder(aPlaceholder.serviceDay, aPlaceholder.menuLine)
            .subscribe(aResponseXBVO => {
                aPlaceholder.orderPlaced = true;
                this.loadPlaceholders();
                this.getOrderDetails();
                this.refreshDayPlaceholders(aDay, aPlaceholder);
                this.toastInfo(this.v('orderPlaced'));
            });
    }

    /**
     * REST - calls
     * @private
     */
    onCancelOrder(aPlaceholder: BsaOrderPlaceholderXBVO) {
        if (aPlaceholder.disableRemoval || !aPlaceholder.orderPlaced) {
            return;
        }
        this.restClient
            .cancelOrder(aPlaceholder.serviceDay, aPlaceholder.menuLine)
            .subscribe(aResultXBVO => {
                aPlaceholder.orderPlaced = false;
                this.loadPlaceholders();
                this.getOrderDetails();
                this.toastInfo(this.v('orderCancelled'));
            });
    }

    /**
     * Unchecks placeholders (instead data reload)
     *
     * @param aIncomingPlaceholder
     */
    private refreshDayPlaceholders(aDay: OrderDayModel, aIncomingPlaceholder: BsaOrderPlaceholderXBVO) {
        let pSingleOrderMealTypes: MealTypeEnum[] = [MealTypeEnum.LUNCH, MealTypeEnum.MILK];
        if (pSingleOrderMealTypes.includes(aIncomingPlaceholder.mealType)) {
            let pIncomingMealType = aIncomingPlaceholder.mealType;
            let pIncomingDate = DateUtilsBs.clearTime(aIncomingPlaceholder.serviceDay);
            for (let pOther of aDay.placeholders) {
                if (pIncomingMealType == pOther.mealType && pOther.menuLine != aIncomingPlaceholder.menuLine) {
                    pOther.orderPlaced = false;
                }
            }
        }
    }


    /**
     * Converts model
     * @param aResultXBVO
     * @private
     */
    private convertModelPlaceholders(aResultXBVO: BsaOrderPlaceholdersXBVO) {
        this.modelOrdersWeek = new OrderWeekModel();
        this.modelPrepaidSaldo = null;
        if (aResultXBVO == null) {
            return;
        }

        from(aResultXBVO.orderPlaceholder)
            .pipe(
                groupBy((aPlaceholder) => aPlaceholder.serviceDay.toISOString()), /* groupBy needs simple type */
                mergeMap(group => {
                    return zip(of(group.key), group.pipe(toArray()));
                })
            ).subscribe((aZip) => {
            let pModel = OrderDayModel.create(DateUtilsBs.parseISO8601(aZip[0]), aZip[1]);
            this.modelOrdersWeek.daysModel.push(pModel);
            this.modelPrepaidSaldo = aResultXBVO.prepaidSaldo;
        });
    }

    private convertSaldo(saldoDetails: SaldoDetailsXBVO) {
      this.saldoDetails = saldoDetails;
    }

    /**
     * Action.
     */
    public onWeekChanged() {
        this.loadPlaceholders();
        this.getOrderDetails();
        this.saveUxState();
    }

    /**
     * Action.
     */
    public onWeekPrev() {
        let pYearWeek = DateUtilsBs.addWeek(this.modelYear, this.modelWeek, -1);
        this.modelYear = pYearWeek[0];
        this.modelWeek = pYearWeek[1];
        this.onWeekChanged();
    }

    /**
     * Action.
     */
    public onWeekNext() {
        let pYearWeek = DateUtilsBs.addWeek(this.modelYear, this.modelWeek, 1);
        this.modelYear = pYearWeek[0];
        this.modelWeek = pYearWeek[1];
        this.onWeekChanged();
    }


    /**
     * Action.
     */
    public onWeekCurrent() {
        let pToday = new Date();
        this.modelYear = DateUtilsBs.getYear(pToday);
        this.modelWeek = DateUtilsBs.getWeek(pToday);
        this.onWeekChanged();

        this.toastInfo("Aktuelle Kalenderwoche gewählt!")

    }

    /**
     * Action.
     * @param aPlaceholder
     */
    public onGotoDetails(aPlaceholder: BsaOrderPlaceholderXBVO) {
        this.orderState.saveSelectedPlaceholder(aPlaceholder);
        this.router.navigate(['/order-details'])
    }

    /**
     * Splits.
     * @param aMenuName
     */
    splitNames(aMenuName: string): string[] {
        return StringUtils.splitMenuName(aMenuName);
    }

    /**
     *
     * @private
     */
    private saveUxState() {
        this.orderState.saveKW(this.modelYear, this.modelWeek);
    }
}
